import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { StateService } from '@uirouter/core';
import { Subscription } from 'rxjs';

import { Accessibility, CimaCore, Cloudinary, Formatter, Util, XmStore, XmStoreUtil } from 'core/services';
import { StoreAction } from 'store/actions';
import { DataPlanType, PlanSwitchType, UnlPlanSubgroup, UnlPlanSubgroupName } from 'my-account/constants';
import { User } from 'store/user/models';

import { CmsDataDetail, CmsLineDataPlanCollection } from 'store/cms/models';
import { AccountPlanEligibility } from 'store/user/models/account-plan-eligibiity';
import { AccountSummary, LineSummary, PlanSummary } from 'store/account-summary/models';
import { PlanEligibility } from 'store/line/models';
import { PromotionOffer } from 'core/constants';
import { DiscountPromoCollection, DiscountPromotion } from 'store/promotions/discount-promotion/models';

@Component({
    selector: 'xm-plan-detail',
    styleUrls: ['./plan-detail.scss'],
    templateUrl: './plan-detail.html',
    standalone: false
})
export class XmPlanDetailComponent implements OnInit, OnDestroy {
    @Input() public plan: PlanSummary;
    @Input() public activePlan: boolean;
    @Input() public planData: CmsDataPlanDetails;
    @Input() public isActiveLine: boolean;
    @Input() public currentLine: LineSummary;
    @Input() public isLLEEnabledMemberOrViewer: boolean;

    public inUse: string;
    public featureTitle: string;
    public planName: string;
    public planPrice: string;
    public inUseIcon: MediaImageOptions;
    public miniIcon: MediaImageOptions;
    public switchPlanLabel: string;
    public changeSharedDataBtnLabel: string;
    public planEligibilityFail: boolean = false;
    public planEligibilityPromise: Promise<void>;
    public planEligibilityResolve: (value?: void | PromiseLike<void>) => void;
    public errorText: string;
    public cmsDataDetailPageData: CmsDataDetail;
    public accountSummary: AccountSummary;
    public dataFeatures: CmsDetailPlanFeature[];
    public isBusinessUser: boolean;
    public user: User;
    public promoResponse: DiscountPromoCollection;
    public linePromotion: DiscountPromotion[];
    public accountPromotion: DiscountPromotion[];
    public tradeInDDAPromo: DiscountPromotion[];
    public extendedPromoDollar: DiscountPromotion[];
    public disablePromiseButton: boolean = false;
    public switchToCbmLevel5Cta: string;
    public switchToCbmLevel5TabletCta: string;
    public showPlanSixTabletSection: boolean;
    public dataPlanPageData: CmsDataPlanDetails[];

    // Discount Engine promotions
    public promotionIcon: MediaImageOptions;
    public shouldAccountShowPromotion: boolean = false;
    public servicePromotions: CmsServicePromotions;
    public promotionAccountHeader: string;
    public promotionAccountSubheader: string;

    // Feature Flag for Discount Engine
    public isPlanSixCbmForLevel5Enabled: boolean;

    public lineSummary: LineSummary;
    private state: StateService;
    private xmStore: XmStore;
    private subscriptions: Subscription[] = [];
    private cimaCore: CimaCore;

    constructor(accessibility: Accessibility, xmStore: XmStore, state: StateService, cimaCore: CimaCore) {
        Object.assign(this, { accessibility, xmStore, state, cimaCore });
    }

    public ngOnInit(): void {
        // Include this to support Every CBM customer can see this on 11/15 release - No matter trial token or feature flag
        this.isBusinessUser = this.cimaCore.isBusinessUser;

        this.miniIcon = {
            mobile: Cloudinary.generateMediaFromCms(this.planData.miniIcon)
        };

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<User>(User), (user: User) => {
            this.user = user;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsLineDataPlanCollection>(CmsLineDataPlanCollection), (result: CmsLineDataPlanCollection) => {
            this.dataPlanPageData = result.plans;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsDataDetail>(CmsDataDetail), (pageData: CmsDataDetail) => {
            this.cmsDataDetailPageData = pageData;
            this.inUseIcon = {
                mobile: Cloudinary.generateMediaFromCms(this.cmsDataDetailPageData.inUseIcon)
            };

            this.inUse = this.cmsDataDetailPageData.inUse;
            this.featureTitle = this.cmsDataDetailPageData.featureTitle;
            this.changeSharedDataBtnLabel = this.cmsDataDetailPageData.changeSharedDataBtnLabel;
            this.errorText = this.cmsDataDetailPageData.errorText;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<AccountSummary>(AccountSummary), (accountSummary: AccountSummary) => {
            this.accountSummary = accountSummary;
            this.lineSummary = this.accountSummary.getLineByKey(this.state.params.line_id);
            this.isPlanSixCbmForLevel5Enabled = this.isBusinessUser && this.accountSummary.level5Customer;
            this.planPrice = this.buildPlanPrice();
            const currentPlanName: string = this.buildPlanName();   
            this.planName = this.accountSummary.level4Customer && this.plan?.isUnlimited && this.lineSummary.isUnlBasic ? UnlPlanSubgroupName.INTRO : currentPlanName;
            if (this.isPlanSixCbmForLevel5Enabled) {
                if (this.lineSummary.isScheduledSwitch) {
                    this.switchToCbmLevel5Cta = this.cmsDataDetailPageData.manageUnlimitedCta;
                    this.switchToCbmLevel5TabletCta = this.cmsDataDetailPageData.manageUnlimitedTabletCta;
                } else {
                    this.switchToCbmLevel5Cta = this.lineSummary.isUnlPremium ? this.cmsDataDetailPageData.switchToUnlIntroCta : this.cmsDataDetailPageData.switchToUnlPremiumCta;
                    this.switchToCbmLevel5TabletCta = this.lineSummary.isUnlPremium ? this.cmsDataDetailPageData.switchToTabletUnlIntroCta : this.cmsDataDetailPageData.switchToTabletUnlPremiumCta;
                }
            }
            this.dataFeatures = this.getDataFeatures();
            this.showPlanSixTabletSection = this.accountSummary.level5Customer && this.lineSummary.isTablet;

            if (this.activePlan && this.lineSummary.isUnlimited) {
                this.getPromotionContent(this.lineSummary);
            }
            this.disablePromiseButton = this.lineSummary.isScheduledSwitch ? !this.lineSummary.isScheduledSwitchSubGroup : this.lineSummary.isScheduledSwitch;
        }));
        if (this.accountSummary.level5Customer) {
            let description: string;
            if (this.lineSummary.isStandaloneWatch) {
                description = this.planData.planDescription; 
            } else if (this.isPlanSixCbmForLevel5Enabled) {
                description = this.planData.planSixCbmDescription;
            } else {
                description = this.planData.planSixDescription;
            }
            this.planData.planDescription = description;
            this.planData.notActivePlanDescription = this.isPlanSixCbmForLevel5Enabled ? this.planData.notActivePlanSixCbmDescription : this.planData.notActivePlanSixDescription;
        }
        
        this.setSwitchLabel();
    }

    public setSwitchLabel(): void {
        if (this.accountSummary.level5Customer) {
            if (!this.activePlan && this.planData.planType !== DataPlanType.BY_THE_GIG_SHARED) {
                this.switchPlanLabel = `${this.cmsDataDetailPageData.upgradePlanLabel} to ${this.planData.planName}`;
            } else {
                this.switchPlanLabel = `${this.cmsDataDetailPageData.switchPlanLabel} to ${this.planData.planName}`;
            }
        }
        if (this.lineSummary.isScheduledSwitch && !this.lineSummary.isScheduledSwitchSubGroup) {
            this.switchPlanLabel = this.cmsDataDetailPageData.cancelSwitchLabel;
        } else {
            this.switchPlanLabel = `${this.cmsDataDetailPageData.switchPlanLabel} to ${this.planData.planName}`;
        }
    }

    public generateFeatureIcon(image: CmsMediaImage): MediaImageOptions {
        return {
            mobile: Cloudinary.generateMediaFromCms(image)
        };
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    public openModal(): Promise<void> {
        this.planEligibilityFail = false;

        return this.getLineSwitchEligibility();
    }

    public openSwitchSharedDataModal(): void {
        this.planEligibilityPromise = this.xmStore.fetchPromise<AccountPlanEligibility>(StoreAction.GET_ACCOUNT_PLAN_ELIGIBILITY)
            .then(() => {
                this.state.go('account.activity.bucket-switch.bucket-picker');
            });
    }

    public promotionLineHeader(linePromotion: DiscountPromotion): string {
        const promoHeader: string = linePromotion.promoBOID === PromotionOffer.BYOD_LL_SERP
            ?  this.servicePromotions.promoBYOLineHeader 
            : this.servicePromotions.promoLineHeader;  
        
        return this.formatPromotionData(promoHeader, linePromotion.billerInfo?.monthlyPromotionValue);
    }

    public promotionLineSubheader(linePromotion: DiscountPromotion): string {
        return this.formatPromotionData(this.servicePromotions.BYOSubHeader, linePromotion.billerInfo?.monthlyPromotionValue, linePromotion.billerInfo?.promotionExpiration);
    }

    public promoAccountSubheader(accountPromotion: DiscountPromotion): string {
        return this.formatPromotionData(this.servicePromotions.subHeader, accountPromotion.billerInfo?.monthlyPromotionValue, accountPromotion.billerInfo?.promotionExpiration);
    }

    public promotionTradeInDDAHeader(tradeInPromotion: DiscountPromotion): string {
        return this.formatPromotionData(this.servicePromotions.promoTradeInDDAHeader, tradeInPromotion.billerInfo?.monthlyPromotionValue.toString());
    }

    public promotionExtendedDollarDDAHeader(extendedDollarPromo: DiscountPromotion): string {
        return this.formatPromotionData(this.servicePromotions.promoExtendedDollarDDAHeader, extendedDollarPromo.billerInfo?.monthlyPromotionValue.toString());
    }

    public isUnlScheduledSwitch(): boolean {
        const planType: string = this.dataPlanPageData.find((data: CmsDataPlanDetails) => data.planCode.includes(this.lineSummary.scheduledSwitch.planCode)).planType;

        return planType === DataPlanType.UNLIMITED;

    }

    private getLineSwitchEligibility(): Promise<void> {
        const switchType: PlanSwitchType = (this.lineSummary.isTablet && Boolean(this.plan.isTablet) && this.isPlanSixCbmForLevel5Enabled) ? PlanSwitchType.INTRA
            : (this.lineSummary.isUnlimited && Boolean(this.plan.isUnlimited)) ? PlanSwitchType.INTRA_UNL_GROUP
                : PlanSwitchType.INTER_GROUP;

        return this.planEligibilityPromise = this.xmStore.fetchPromise<PlanEligibility>(StoreAction.GET_LINE_LEVEL_PLAN_ELIGIBILITY, {
            params: { lineKey: this.state.params.line_id, switchType: switchType },
            filters: { lineKey: this.state.params.line_id }
        }).then((planEligibility: PlanEligibility) =>
            this.checkNextStep(planEligibility)
        ).catch(() => {
            this.planEligibilityFail = true;

            return this.planEligibilityResolve();
        });
    }

    private checkNextStep(planEligibility: PlanEligibility): Promise<void> {
        if (planEligibility.isInEligible) {
            this.state.go('account.activity.details.plan-switch.ineligible-overage', { line_id: this.state.params.line_id });
        } else if (this.lineSummary.isUnlimited) {
            if ((this.accountSummary.level4Customer || (this.accountSummary.level5Customer && this.user.account.isSegmentFive)) && this.plan.isUnlimited) {
                planEligibility.isIneligibleScheduledPlan ? this.state.go('account.activity.details.plan-switch.unl-tiers-cancel-downgrade', { line_id: this.state.params.line_id, plan_code: planEligibility.eligiblePlan.planCode })
                    : this.state.go('account.activity.details.plan-switch.unl-tiers-review', { line_id: this.state.params.line_id, plan_code: planEligibility.eligiblePlan.planCode });
            } else if (this.accountSummary.level5Customer) {
                this.state.go('account.activity.details.plan-switch.review', { line_id: this.state.params.line_id, plan_code: planEligibility.eligiblePlan.planCode });
            } else {
                this.state.go('account.activity.details.plan-switch.bucket-picker', { line_id: this.state.params.line_id });
            }
        } else if (this.lineSummary.isTablet && this.isPlanSixCbmForLevel5Enabled) {
            planEligibility.isIneligibleScheduledPlan ? this.state.go('account.activity.details.plan-switch.unl-tiers-cancel-downgrade', { line_id: this.state.params.line_id, plan_code: planEligibility.eligiblePlan.planCode })
                : this.state.go('account.activity.details.plan-switch.unl-tiers-review', { line_id: this.state.params.line_id, plan_code: planEligibility.eligiblePlan.planCode });
        } else {
            if (this.accountSummary.level4Customer || this.accountSummary.level5Customer) {
                if (planEligibility.plans.length === 1 && planEligibility.plans[0].planCode.startsWith('8893254')) {
                    this.state.go('account.activity.details.plan-switch.review', { line_id: this.state.params.line_id, plan_code: planEligibility.eligiblePlan.planCode });
                } else {
                    this.state.go('account.activity.details.plan-switch.unl-tiers-review', { line_id: this.state.params.line_id, plan_code: planEligibility.eligiblePlan.planCode });
                }
            } else {
                this.state.go('account.activity.details.plan-switch.review', { line_id: this.state.params.line_id, plan_code: planEligibility.eligiblePlan.planCode });
            }
        }

        return Promise.resolve();
    }

    private buildPlanName(): string {
        // if Current line is on Unlimited and account has shared data, build Plan Name
        return !this.accountSummary.level5Customer && this.accountSummary.hasSharedPlan && this.lineSummary.isUnlimited && this.planData.planType === DataPlanType.BY_THE_GIG_SHARED ?
            `${this.planData.planName}, ${Util.mbToGb(this.plan.dataPassSize, true)} GB` : this.planData.planName;
    }

    private buildPlanPrice(): string {
        if (this.planData.planType === DataPlanType.BY_THE_GIG_SHARED) {
            if (this.accountSummary.level5Customer || this.isPlanSixCbmForLevel5Enabled) {
                return this.cmsDataDetailPageData.dataOptionsPlanDecriptions.byTheGigPlanDescription.replace('[LINE_COST]', this.activePlan ? 
                    this.lineSummary.planPrice && this.lineSummary.planPrice.toString() : 
                    this.lineSummary.postSwitch.lineCost.toString());
            } else {
                // If BTG plan is not Active and account doesn't have shared data then show different static copy for LEVEL 2 and LEVEL 3 customer
                return this.activePlan ? `${Util.mbToGb(this.plan.dataPassSize, true)} GB` : this.accountSummary.hasSharedPlan ? `$${this.lineSummary.postSwitch.lineCost.toString()}/mo`
                    : this.cmsDataDetailPageData.dataOptionsPlanDecriptions.byTheGigPlanNotInUse.replace('[LINE_COST]', this.lineSummary.postSwitch.lineCost.toString());
            }
        } else {
            // RUP is only valid for LEVEL 4 and LEVEL 3 PHONE customer only
            if (this.lineSummary.isDevice) {
                const planSubheader: string = this.getPlanPricing();

                return planSubheader.replace('[NO_OF_LINES]', this.lineSummary.numberOfLinesInThePlan && this.lineSummary.numberOfLinesInThePlan.toString())
                    .replace('[MULTILINE_SUFFIX]', this.lineSummary.numberOfLinesInThePlan > 1 ? 's' : '')
                    // This is when user switch BTG to UNL with RUP so replace with lineCost
                    .replace('[RUP_FEATURE_SWITCH_PLAN_PRICE]', this.lineSummary.postSwitch.lineCost && this.lineSummary.postSwitch.lineCost.toString())
                    // This is when user switch UNL to BTG with RUP so replca with planGroupCost
                    .replace('[RUP_CURRENT_PLAN_PRICE]', this.lineSummary.planPrice && this.lineSummary.planPrice.toString());
            } else if (this.lineSummary.isStandaloneWatch) {
                return `$${this.currentLine.planPrice.toString()}/mo`;
            } else {
                // Common copy for Unlimited plan for Seg 2 and Seg 3 Tablet customer Only
                return this.cmsDataDetailPageData.dataOptionsPlanDecriptions.unlimitedPlanDescription;
            }
        }
    }

    private getPlanPricing(): string {
        if (this.activePlan) {
            if (this.accountSummary.level2Customer || this.accountSummary.level3Customer) {
                return this.cmsDataDetailPageData.dataOptionsPlanDecriptions.regressiveUnlimitedPlanInUseLevel2or3;
            }
            if (this.accountSummary.level4Customer) {
                return this.cmsDataDetailPageData.dataOptionsPlanDecriptions.regressiveUnlimitedPlanInUseLevel4;
            } 
            if (this.accountSummary.level5Customer) {
                return this.cmsDataDetailPageData.dataOptionsPlanDecriptions.regressiveUnlimitedPlanInUseLevel5;
            }
        }

        if (!this.isBusinessUser) {
            return this.accountSummary.level5Customer ? this.cmsDataDetailPageData.dataOptionsPlanDecriptions.regressiveUnlimitedPlanNotInUseLevel5 
                : this.cmsDataDetailPageData.dataOptionsPlanDecriptions.regressiveUnlimitedPlanNotInUse;
        } else {
            return this.isPlanSixCbmForLevel5Enabled ? this.cmsDataDetailPageData.dataOptionsPlanDecriptions.planSixCbmUnlimitedPlanNotInUseDescription 
                : this.cmsDataDetailPageData.dataOptionsPlanDecriptions.regressiveUnlimitedPlanNotInUse;
        }
    }

    private getDataFeatures(): CmsDetailPlanFeature[] {
        if (this.lineSummary.isScheduledSwitch && this.isUnlScheduledSwitch() && this.planData.planType === DataPlanType.UNLIMITED) {
            if (this.accountSummary.level4Customer) {
                const features: CmsDetailPlanFeature[] = this.planData.scheduledSwitchdataFeatures;

                return features && features.map((feature: CmsDetailPlanFeature) => ({
                    ...feature,
                    header: feature.header.replace('[SCHEDULED_SWITCH_PLAN]', this.getUnlimitedSubPlanName())
                        .replace('[Date]', Formatter.formatMonthDate(this.lineSummary.scheduledSwitch.scheduleDate)),
                    subheader: feature.subheader.replace('[SCHEDULED_SWITCH_PLAN]', this.getUnlimitedSubPlanName())
                }));
            } if (this.accountSummary.level5Customer) {
                this.changeSharedDataBtnLabel = this.cmsDataDetailPageData.scheduledSwitchLevel5ManageCta;
                const features: CmsDetailPlanFeature[] = this.lineSummary?.scheduledSwitch?.planSubgroup ? this.planData.featuresForLevel5ScheduledSwitch : this.planData.scheduledSwitchByTheGigdataFeatures;

                if (this.isPlanSixCbmForLevel5Enabled) {
                    return features && features.map((feature: CmsDetailPlanFeature) => ({
                        ...feature,
                        header: feature.header.replace('[SCHEDULED_SWITCH_PLAN]', this.getUnlimitedSubPlanName())
                            .replace('[Date]', Formatter.formatMonthDate(this.lineSummary.scheduledSwitch.scheduleDate)),
                        subheader: feature.subheader.replace('[SCHEDULED_SWITCH_PLAN]', this.getUnlimitedSubPlanName())
                            .replace('[Date]', Formatter.formatMonthDate(this.lineSummary.scheduledSwitch.scheduleDate))
                    }));
                }
 
                return features && features.map((feature: CmsDetailPlanFeature) => ({
                    ...feature,
                    header: feature.header.replace('[CURRENT_PLAN]', this.getPlanName()).replace('[Date]', Formatter.formatMonthDate(this.lineSummary.scheduledSwitch.scheduleDate)),
                    subheader: feature.subheader.replace('[CURRENT_PLAN]', this.getPlanName()).replace('[Date]', Formatter.formatMonthDate(this.lineSummary.scheduledSwitch.scheduleDate))
                }));
            }
        }
        if (this.accountSummary.level5Customer) {
            if (this.lineSummary.isUnlBasic) {
                this.featureTitle = this.isPlanSixCbmForLevel5Enabled ? this.cmsDataDetailPageData.unlimitedPremium : this.cmsDataDetailPageData.unlimitedPlus;          
            } else if (this.lineSummary.isUnlPlus) {
                this.featureTitle = this.cmsDataDetailPageData.unlimited;
            } else if (this.lineSummary.isUnlPremium) {
                this.featureTitle = this.cmsDataDetailPageData.unlimitedIntro;
            }
            const features: CmsDetailPlanFeature[] = this.isPlanSixCbmForLevel5Enabled ? this.planData.featuresForLevel5Cbm : this.planData.featuresForLevel5;

            return features;
        } else {
            return this.planData.dataFeatures;
        }
    }

    private getUnlimitedSubPlanName(): string {
        switch (this.lineSummary.scheduledSwitch.planSubgroup) {
            case UnlPlanSubgroup.BASIC:
                return this.cmsDataDetailPageData.unlimitedIntro;
            case UnlPlanSubgroup.PLUS:
                return this.cmsDataDetailPageData.unlimitedPlus;
            case UnlPlanSubgroup.PREMIUM:
                return this.cmsDataDetailPageData.unlimitedPremium;
        }
    }

    private getPromotionContent(lineSummary: LineSummary): void {
        XmStoreUtil.defaultCatch(this.xmStore.findPromise<DiscountPromoCollection>(StoreAction.GET_DISCOUNT_PROMOTION, DiscountPromoCollection)
            .then((response: DiscountPromoCollection) => {
                this.promoResponse = response;
                this.servicePromotions = this.cmsDataDetailPageData.servicePromotions;
                this.promotionIcon = {
                    mobile: Cloudinary.generateMediaFromCms(this.servicePromotions.icon)
                };

                if (this.promoResponse.isAccountPromoEligible) {
                    this.accountPromotion = this.promoResponse.accountPromoEligible;
                    this.shouldAccountShowPromotion = this.promoResponse.isAccountPromoEligible;
                }

                if (this.promoResponse.isLinePromoUNLFREEEligible || this.promoResponse.isLinePromoBYOEligible) {
                    const lineUNLPromo: DiscountPromotion[] = this.promoResponse.linePromoUNLEligible.filter((promo: DiscountPromotion) => promo.lineId === lineSummary.id.key || promo.lineId === lineSummary.id.value);
                    const lineBYOPromo: DiscountPromotion[] = this.promoResponse.linePromoBYOEligible.filter((promo: DiscountPromotion) => promo.lineId === lineSummary.id.key || promo.lineId === lineSummary.id.value);
                    
                    this.linePromotion = [...lineUNLPromo, ...lineBYOPromo];
                }
                
                if (this.promoResponse.tradeInDDAPromoEligible) {
                    this.tradeInDDAPromo = this.promoResponse.tradeInDDAPromoEligible.filter((promo: DiscountPromotion) => promo.lineId === lineSummary.id.key || promo.lineId === lineSummary.id.value);
                }

                if (this.promoResponse.extendedPromoDollarEligible) {
                    this.extendedPromoDollar = this.promoResponse.extendedPromoDollarEligible.filter((promo: DiscountPromotion) => promo.lineId === lineSummary.id.key || promo.lineId === lineSummary.id.value);
                }
            })
        );
    }

    private formatPromotionData(text: string, amount: string, date?: string): string {
        return text.replace('[AMOUNT]', amount).replace('[DATE]', Formatter.formatMonthDateYear(date));
    }

    private getPlanName(): string {
        switch (this.lineSummary?.planSubgroup) {
            case UnlPlanSubgroup.BASIC:
                return this.cmsDataDetailPageData.unlimited;
            case UnlPlanSubgroup.PLUS:
                return this.cmsDataDetailPageData.unlimitedPlus;
            default:
                return this.lineSummary?.planSubgroup;
        }
    }

}
